import { Dialog } from "@mui/material";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ReactFlow, {
    Background,
    Controls
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useGetAddedIntegrations } from "../../api/integrations";
import { useDeleteNodeById, useDeleteNodeConnection, useGetTestRunInputsForEachNode, useInsertNodeBetween, useSaveNode, useSaveNodeConnection, useSaveNodeWithPatch, useUpdateNodeConnection } from "../../api/nodeApi";
import { useGetWorkflowById, usePublishWorkflow, useTurnWorkflowOnOrOff, useUpdateWorkflow } from "../../api/workflowsApi";
import '../../styles/NewWorkflow.css';
import { AddCircle } from "../../svgUrls";
import { Node, WorkflowNode } from "../../types/workflow";
import { useWorkflowId } from "../../utils/misc";
import getAvailableNodesForVariables from "../../utils/workflow";
import TriggersList from "../TriggersList";
import FloatingWorkflowBoxPanel from "../WorkflowBox/FloatingWorkflowBoxPanel";
import useDataMappings from "./useDataMappings";
import useExecutionConditions from "./useExecutionConditions";
import useFetchWorkflowNodes from "./useFetchWorkflowNodex";
import useNodeConfigs from "./useNodeConfigs";
import useNodeConnections from "./useNodeConnections";
import useReactFlowManager from './useReactFlowManager';
import useSelectedTestRunInstance from './useSelectedTestRunInstance';
import useTestRunInstances from "./useTestRunInstances";
import ConnectionContextMenu from "./ConnectionContextMenu";
import { canMakeConnectionOptional } from "../../utils/connectionValidation";
import { Edge } from "reactflow";
import { getShouldRefetchConfig } from "./utils";
import WorkflowHeader from './WorkflowHeader';

const getConfig = (isFirstNode: boolean, nodeConfig: Record<string, string | null>) => {
    const hasValues = typeof nodeConfig === 'object' && Object.keys(nodeConfig).some(key => !!nodeConfig[key]);
    if (isFirstNode && hasValues) {
        return JSON.stringify(nodeConfig);
    }
    return '';
};

const useNewWorkflowMutations = () => {
    const { mutate: saveNode } = useSaveNode();
    const { mutate: saveNodeWithPatch } = useSaveNodeWithPatch();
    const { mutate: saveNodeConnection } = useSaveNodeConnection();
    const { mutate: insertNodeBetween } = useInsertNodeBetween();
    return {
        saveNode,
        saveNodeWithPatch,
        saveNodeConnection,
        insertNodeBetween,
    }
};

const NewWorkflow = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const trigger = searchParams.get('trigger');

    useEffect(() => {
        if (trigger === 'true') {
            setSearchParams({ trigger: 'false' });
        }
    }, [trigger]);

    const workflowId = useWorkflowId();
    const {
        saveNode,
        saveNodeWithPatch,
        saveNodeConnection,
        insertNodeBetween,
    } = useNewWorkflowMutations();

    const {
        nodeResponseConfigs,
        setNodeResponseConfigs,
        nodeConfigs,
        setNodeConfigs,
        getNodeConfigsForNodeFromRef,
        setNodeConfigsForNodeInRef,
    } = useNodeConfigs();

    const {
        fetchNodeConnections,
        nodeConnections,
        updateNodeConnection: updateNodeConnectionLocal,
        removeNodeConnection: removeNodeConnectionLocal
    } = useNodeConnections();
    
    const { mutate: updateNodeConnection } = useUpdateNodeConnection();
    const { mutate: deleteNodeConnection } = useDeleteNodeConnection();

    const {
        testRunInstances,
        setTestRunInstances,
    } = useTestRunInstances();

    const {
        executionConditionsForNodes,
        setExecutionConditionsForNodes,
    } = useExecutionConditions();

    const { data: currentWorkflow } = useGetWorkflowById({ workflowId: workflowId });
    const [workflowNodes, setWorkflowNodes] = useState<WorkflowNode[]>([]);
    const { mutate: getTestRunInputs, data: testRunInputs } = useGetTestRunInputsForEachNode();
    const { mutate: turnWorkflowOnOrOff } = useTurnWorkflowOnOrOff();

    useEffect(() => {
        if (workflowNodes && workflowNodes.length && workflowNodes[0]?.node.id) {
            getTestRunInputs({ nodeId: workflowNodes[0].node.id, reset: false });
        }
    }, [workflowNodes]);

    const [openTriggerDialog, setOpenTriggerDialog] = useState(trigger === 'true');
    const prevNodeRef = useRef<WorkflowNode | null>(null);
    const isInsertingNodeBetween = useRef({
        startNodeId: null,
        endNodeId: null,
        enabled: false,
    } as {
        startNodeId: string | null;
        endNodeId: string | null;
        enabled: boolean;
    });

    const [selectedWorkflowNodeId, setSelectedWorkflowNodeId] = useState<string | null>(null);
    const [isFloatingPanelOpen, setIsFloatingPanelOpen] = useState(false);
    
    // Context menu state for connection optional toggle
    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        edge: Edge | null;
    } | null>(null);

    const {
        modifyDataMappings,
        modifySingleDataMapping,
        getNodeDataMappingsFromConfig
    } = useDataMappings(currentWorkflow, getNodeConfigsForNodeFromRef);

    const { data: integrations } = useGetAddedIntegrations();

    const { refreshWorkflowNodes } = useFetchWorkflowNodes({
        setWorkflowNodes,
        setExecutionConditionsForNodes,
        fetchNodeConnections,
    });

    const getIsTrigger = (nodeId: string) => {
        return !!nodeId && !nodeConnections.find((nodeConnection) => nodeConnection.end_node.id == nodeId)
    }

    const saveNewNode = (isIntermittentSave: boolean, id: string, chosenAction: string, workflowType: string, refreshSavedRunConfig: (nodeId: string) => void, cb?: () => void) => {
        if (currentWorkflow) {
            if (id) {
                const allNodes = cloneDeep(workflowNodes);
                const nodeIndex = allNodes.findIndex((node) => node?.node.id == id);
                const isTrigger = getIsTrigger(id);
                const { run_config } = getNodeConfigsForNodeFromRef(id);
                const config = getConfig(isTrigger, run_config || {});
                saveNodeWithPatch({
                    ...workflowNodes[nodeIndex].node as Node,
                    name: workflowNodes[nodeIndex].node?.name || `${workflowType} - ${chosenAction}`,
                    action: chosenAction,
                    config,
                }, {
                    onSuccess: (node: Node) => {
                        const allNodes = cloneDeep(workflowNodes);
                        const nodeIndex = allNodes.findIndex((node) => node?.node.id == id);

                        const prevAction = allNodes[nodeIndex]?.node?.action;
                        allNodes[nodeIndex] = { ...(allNodes[nodeIndex] ? allNodes[nodeIndex] : []), node: node };
                        if (nodeIndex > 0) {
                            modifyDataMappings(allNodes[nodeIndex], '', refreshSavedRunConfig);
                        }
                        setWorkflowNodes(cloneDeep(allNodes));
                        const dataMappingsForNode = getNodeDataMappingsFromConfig(currentWorkflow, run_config, allNodes[nodeIndex].nodeConnection!);
                        const shouldRefetchConfig = getShouldRefetchConfig(allNodes[nodeIndex], dataMappingsForNode);
                        if ((!prevAction || shouldRefetchConfig) && typeof refreshSavedRunConfig === 'function' && nodeIndex == 0) {
                            refreshSavedRunConfig(node.id);
                        }

                        if (!isIntermittentSave) {
                            toast.success("Saved successfully");
                        }

                        if (cb) cb()
                    }
                });
            } else {
                saveNode({
                    name: `${workflowType} - ${chosenAction}`,
                    workflow: currentWorkflow,
                    action: chosenAction,
                    service: workflowType,
                }, {
                    onSuccess: (node) => {
                        const allNodes = cloneDeep(workflowNodes);
                        allNodes.push({ node });
                        setWorkflowNodes(cloneDeep(allNodes));

                        if (prevNodeRef.current?.node) {
                            createConnection(prevNodeRef.current?.node as Node, node, allNodes, () => {
                                const selectedNode = allNodes.find((_node) => _node.node.id == node.id);
                                setSelectedWorkflowNodeId(selectedNode?.node.id || '');
                                setIsFloatingPanelOpen(true);
                                if (node.service === 'paths') {
                                    refreshWorkflowNodes()
                                }
                            });
                        } else {
                            setSelectedWorkflowNodeId(node.id || '');
                            setIsFloatingPanelOpen(true);
                        }
                    }
                });
            }
        }
    }

    const createConnection = (startNode: Node, endNode: Node, allNodes: WorkflowNode[], cb: () => void) => {
        if (currentWorkflow) {
            saveNodeConnection({
                workflow: currentWorkflow,
                start_node: startNode,
                end_node: endNode,
            }, {
                onSuccess: (nodeConnection) => {
                    const nodeIndex = allNodes.findIndex((node) => node?.node.id == endNode.id);
                    allNodes[nodeIndex].nodeConnection = nodeConnection;
                    allNodes[nodeIndex].node = endNode;
                    setWorkflowNodes(cloneDeep(allNodes));
                    fetchNodeConnections();
                    if (cb) {
                        cb()
                    }
                }
            });
        }
    }

    const updateNodeConfigsFromRunInstances = useCallback((nodeId: string, response_config: string) => {
        if (nodeId) {
            setNodeResponseConfigs((nodeResponseConfigs) => {
                try {
                    nodeResponseConfigs[nodeId] = JSON.parse(response_config);
                } catch (e) {
                    console.log("err updateNodeConfigsFromRunInstances", e);
                }
                return cloneDeep(nodeResponseConfigs)
            });
        }
    }, []);

    const {
        selectedRunInstanceId,
        setSelectedRunInstanceId,
    } = useSelectedTestRunInstance({
        nodeId: workflowNodes[0]?.node?.id,
        testRunInputs: testRunInputs?.inputs,
        updateNodeConfigsFromRunInstances,
    });

    const saveConfigField = (nodeId: string, key: string, refreshSavedRunConfig: (nodeId: string) => void, setIsFetchingSavedNodeConfig: (value: boolean) => void) => {
        const isTrigger = getIsTrigger(nodeId);
        const workflowNode = workflowNodes.find(node => node.node.id === nodeId)
        if (!isTrigger && workflowNode) {
            modifySingleDataMapping(workflowNode, key, refreshSavedRunConfig, setIsFetchingSavedNodeConfig);
        } else {
            const { run_config } = getNodeConfigsForNodeFromRef(workflowNode?.node.id || '') || {};
            const config = getConfig(true, run_config || {});

            setIsFetchingSavedNodeConfig(true);
            saveNodeWithPatch({
                ...workflowNode?.node as Node,
                config: config,
            }, {
                onSuccess: () => {
                    refreshSavedRunConfig(nodeId);
                },
                onSettled: () => {
                    setIsFetchingSavedNodeConfig(false);
                }
            });
        }
    }

    const topNodeIdRef = useRef('');

    // Add this new function to handle the add node button click
    const handleAddNodeClick = (parentNode?: WorkflowNode) => {
        prevNodeRef.current = parentNode || null;
        setOpenTriggerDialog(true);
    };

    const { mutate: _deleteNodeById } = useDeleteNodeById();

    const deleteNodeById = async (params: { nodeId: string }) => {
        _deleteNodeById({ nodeId: params.nodeId }, {
            onSuccess: () => {
                refreshWorkflowNodes();
            }
        });
    }

    const getHasChildren = (node: WorkflowNode) => {
        return nodeConnections.some(connection => connection.start_node.id === node.node.id);
    }

    const handleAddNodeBetween = (startNodeId: string, endNodeId: string) => {
        isInsertingNodeBetween.current = {
            startNodeId,
            endNodeId,
            enabled: true,
        }
        setOpenTriggerDialog(true);
    };

    // Handler for edge context menu (right-click)
    const onEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX,
                    mouseY: event.clientY,
                    edge: edge,
                }
                : null,
        );
    }, [contextMenu]);

    // Handler for toggling optional status
    const handleToggleOptional = useCallback((edge: Edge) => {
        if (!edge) return;
        
        // Find the connection from nodeConnections
        const connection = nodeConnections.find(conn => 
            `edge_${conn.start_node.id}_${conn.end_node.id}` === edge.id
        );
        
        if (!connection) {
            toast.error('Connection not found');
            return;
        }

        const currentStatus = connection.is_optional || false;

        // Validation: Check if we can make this optional
        if (!currentStatus) {
            // Trying to make required -> optional
            const canMakeOptional = canMakeConnectionOptional(connection, nodeConnections);
            
            if (!canMakeOptional) {
                toast.error('Multiple connections required to make this optional');
                return;
            }
        }
        // Making optional -> required is always allowed (no validation needed)

        // Call API to update connection
        updateNodeConnection({
            ...connection,
            is_optional: !currentStatus,
        }, {
            onSuccess: (updatedConnection) => {
                // Update local state
                updateNodeConnectionLocal(updatedConnection);
                // Refresh workflow nodes to update the UI
                fetchNodeConnections();
                refreshWorkflowNodes();
                // Close context menu
                setContextMenu(null);
            },
            onError: (error: any) => {
                console.error('Failed to toggle optional status:', error);
            }
        });
    }, [nodeConnections, updateNodeConnection, updateNodeConnectionLocal, fetchNodeConnections, refreshWorkflowNodes]);

    // Handler for deleting a connection
    const handleDeleteConnection = useCallback((edge: Edge) => {
        if (!edge) return;
        
        // Find the connection from nodeConnections
        const connection = nodeConnections.find(conn => 
            `edge_${conn.start_node.id}_${conn.end_node.id}` === edge.id
        );
        
        if (!connection) {
            toast.error('Connection not found');
            return;
        }

        // Call API to delete connection
        deleteNodeConnection({ connectionId: connection.id }, {
            onSuccess: () => {
                // Update local state
                removeNodeConnectionLocal(connection.id);
                // Refresh workflow nodes to update the UI
                fetchNodeConnections();
                refreshWorkflowNodes();
                // Close context menu
                setContextMenu(null);
            },
            onError: (error: any) => {
                console.error('Failed to delete connection:', error);
            }
        });
    }, [nodeConnections, deleteNodeConnection, removeNodeConnectionLocal, fetchNodeConnections, refreshWorkflowNodes]);

    const {
        flowNodes,
        flowEdges,
        onNodesChange,
        onEdgesChange,
        onConnect,
        onConnectStart,
        onConnectEnd,
        nodeTypes,
        edgeTypes,
    } = useReactFlowManager(
        workflowNodes,
        nodeConfigs,
        integrations,
        setSelectedWorkflowNodeId,
        setIsFloatingPanelOpen,
        handleAddNodeClick,
        nodeConnections,
        updateNodeConfigsFromRunInstances,
        setTestRunInstances,
        deleteNodeById,
        () => { },
        setNodeConfigs,
        setNodeResponseConfigs,
        nodeResponseConfigs,
        setNodeConfigsForNodeInRef,
        refreshWorkflowNodes,
        executionConditionsForNodes,
        getHasChildren,
        saveNodeConnection,
        currentWorkflow,
        fetchNodeConnections,
        handleAddNodeBetween
    );

    // memo
    const triggerNode = workflowNodes.find((node) => getIsTrigger(node?.node?.id || ''));

    const getParentIsTrigger = (nodeId: string) => {
        const connections = nodeConnections.filter((nodeConnection) => nodeConnection.end_node.id == nodeId);
        return connections.length == 1 && connections[0].start_node.id == triggerNode?.node?.id;
    }

    const selectedWorkflowNode = workflowNodes.find((node) => node.node.id === selectedWorkflowNodeId);

    const { mutate: updateWorkflow } = useUpdateWorkflow();
    const { refetch: refetchWorkflow } = useGetWorkflowById({ workflowId });

    const { mutate: publishWorkflow, isPending: isPublishLoading } = usePublishWorkflow();

    const isPublishEnabled = workflowNodes.length >= 2 && Object.keys(nodeResponseConfigs).length >= 2;

    useEffect(() => {
        const savedNodeId = localStorage.getItem('nodeId');
        if (savedNodeId) {
            setSelectedWorkflowNodeId(savedNodeId);
            setIsFloatingPanelOpen(true);
            localStorage.removeItem('nodeId');
        }
    }, []);

    return (
        <div className="w-full overflow-hidden flex flex-col">
            <WorkflowHeader
                currentWorkflow={currentWorkflow}
                onUpdateWorkflow={(workflowId, updatedWorkflow) => {
                    updateWorkflow(
                        { workflowId, workflow: updatedWorkflow },
                        {
                            onSuccess: () => {
                                refetchWorkflow();
                                toast.success("Workflow name updated successfully");
                            }
                        }
                    );
                }}
                onPublishWorkflow={() => {
                    publishWorkflow({ workflowId: currentWorkflow?.id || '' });
                }}
                isPublishLoading={isPublishLoading}
                onToggleEnabled={(bool: boolean) => {
                    turnWorkflowOnOrOff({ workflowId: currentWorkflow?.id || '', switchedOff: !bool }, {
                        onSuccess: () => {
                            refetchWorkflow();
                        }
                    });
                }}
                isEnabled={!currentWorkflow?.switched_off}
                isPublishDisabled={!isPublishEnabled}
            />

            {/* Main Content */}
            <div className="overflow-auto flex-1 relative">
                {flowNodes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full">
                        <p className="mb-4 text-gray-600">No workflow nodes available.</p>
                        <AddCircle
                            className="cursor-pointer text-blue-500"
                            onClick={() => handleAddNodeClick()}
                            style={{ fontSize: '48px' }}
                        />
                    </div>
                ) : (
                    <ReactFlow
                        nodes={flowNodes}
                        edges={flowEdges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onConnectStart={onConnectStart}
                        onConnectEnd={onConnectEnd}
                        onEdgeContextMenu={onEdgeContextMenu}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        defaultEdgeOptions={{
                            type: 'custom',
                            animated: true,
                            style: { stroke: '#B1B1B1', strokeWidth: 2 },
                            data: {
                                onAddNode: handleAddNodeBetween
                            }
                        }}
                        style={{
                            backgroundColor: "#F7F9FB",
                            width: '100%',
                            height: '100%'
                        }}
                        draggable={false}
                        panOnScroll
                        defaultViewport={{ x: 100, y: 100, zoom: 1 }}
                    >
                        <Controls />
                        <Background />
                    </ReactFlow>
                )}
                
                {/* Connection Context Menu */}
                {contextMenu && contextMenu.edge && (() => {
                    // Find the connection for validation
                    const connection = nodeConnections.find(conn => 
                        `edge_${conn.start_node.id}_${conn.end_node.id}` === contextMenu.edge?.id
                    );
                    
                    const isOptional = contextMenu.edge.data?.is_optional || false;
                    const canMakeOptional = connection 
                        ? canMakeConnectionOptional(connection, nodeConnections)
                        : false;

                    return (
                        <ConnectionContextMenu
                            anchorPosition={{ top: contextMenu.mouseY, left: contextMenu.mouseX }}
                            isOptional={isOptional}
                            canMakeOptional={canMakeOptional}
                            onClose={() => setContextMenu(null)}
                            onToggleOptional={() => handleToggleOptional(contextMenu.edge!)}
                            onDelete={() => handleDeleteConnection(contextMenu.edge!)}
                        />
                    );
                })()}

                {isFloatingPanelOpen && selectedWorkflowNode && (
                    <FloatingWorkflowBoxPanel
                        isTriggerNode={!!getIsTrigger(selectedWorkflowNode?.node?.id || '')}
                        workflowType={selectedWorkflowNode.node.service || ''}
                        workflowTitle={selectedWorkflowNode.node.name || ''}
                        workflowBoxIndex={workflowNodes.findIndex(node => node.node.id === selectedWorkflowNode?.node?.id) + 1}
                        savedAction={selectedWorkflowNode.node.action || ''}
                        onNodeSave={saveNewNode}
                        nodeIdsAbove={
                            getAvailableNodesForVariables(
                                selectedWorkflowNode,
                                workflowNodes,
                                nodeConnections
                            ).map(node => ({
                                id: node.node.id || '',
                                service: node.node.service || '',
                                name: node.node.name || ''
                            }))
                        }
                        workflowNode={selectedWorkflowNode}
                        nodeConfigs={nodeConfigs}
                        openByDefault={true}
                        shouldDeleteNode={() => { }}
                        updateNodeConfigsFromRunInstances={updateNodeConfigsFromRunInstances}
                        testRunInputs={testRunInputs?.inputs || []}
                        selectedRunInstanceId={selectedRunInstanceId}
                        setSelectedRunInstanceId={setSelectedRunInstanceId}
                        refetchTestRunInputs={getTestRunInputs}
                        setNodeConfigs={setNodeConfigs}
                        nodeResponseConfigs={nodeResponseConfigs}
                        setNodeResponseConfigs={setNodeResponseConfigs}
                        setNodeConfigsForNodeInRef={setNodeConfigsForNodeInRef}
                        getNodeConfigsForNodeFromRef={getNodeConfigsForNodeFromRef}
                        saveConfigField={saveConfigField}
                        executionConditionsForNode={selectedWorkflowNode.node.id ? executionConditionsForNodes[selectedWorkflowNode.node.id] : []}
                        topNodeIdRef={topNodeIdRef}
                        setIsFloatingPanelOpen={setIsFloatingPanelOpen}
                        parentIsTrigger={getParentIsTrigger(selectedWorkflowNode?.node?.id || '')}
                        triggerNodeId={triggerNode?.node.id || ''}
                        refreshWorkflowNodes={refreshWorkflowNodes}
                        setExecutionConditionsForNodes={setExecutionConditionsForNodes}
                        getHasChildren={getHasChildren}
                        workflowNodes={workflowNodes}
                        testRunInstances={testRunInstances}
                    />
                )}
                {/* Trigger Dialog */}
                <Dialog
                    open={openTriggerDialog}
                    onClose={() => {
                        setOpenTriggerDialog(false);
                        isInsertingNodeBetween.current.enabled = false;
                    }}
                    maxWidth='md'
                    fullWidth
                    PaperProps={{
                        className: 'p-4 h-[70vh]'
                    }}
                >
                    <TriggersList
                        setSelectedService={(service) => {
                            setOpenTriggerDialog(false);
                            if (isInsertingNodeBetween.current.enabled && currentWorkflow) {
                                insertNodeBetween({
                                    startNodeId: isInsertingNodeBetween.current.startNodeId || '',
                                    endNodeId: isInsertingNodeBetween.current.endNodeId || '',
                                    node: { service, name: '', workflow: currentWorkflow, action: '' }
                                }, {
                                    onSuccess: (node) => {
                                        setOpenTriggerDialog(false);
                                        isInsertingNodeBetween.current.enabled = false;
                                        setSelectedWorkflowNodeId(node.id || '');
                                        setIsFloatingPanelOpen(true);
                                        refreshWorkflowNodes();
                                    }
                                });
                            } else {
                                saveNewNode(false, '', '', service, () => { });
                            }
                        }}
                        triggerExists={Array.isArray(workflowNodes) && workflowNodes.length > 0}
                    />
                </Dialog>
            </div>
        </div>
    )
}

export default NewWorkflow;